import type { ReactElement } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { useAppSelector } from "app/hooks.ts";
import { selectUser } from "features/auth/authSlice.ts";
import PageVideoPopupHost from "features/pageVideoPopups/PageVideoPopupHost.tsx";
import { selectPermissions } from "features/permissions/permissionsSlice.ts";
import type { PermissionsParsed } from "features/permissions/permissionsTypes.ts";
import { isMobileClient } from "features/auth/utils/mobileClient.ts";
import { isSalesUserRole } from "features/auth/utils/roles.ts";


const PublicRoute = () => {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);
  const location = useLocation();

  const getRequestedPath = () => {
    const fromPath = location.state?.from?.pathname;

    if (!fromPath || !fromPath.startsWith("/") || fromPath === "/login") {
      return null;
    }

    const fromSearch = location.state?.from?.search ?? "";
    const fromHash = location.state?.from?.hash ?? "";

    return `${fromPath}${fromSearch}${fromHash}`;
  };

  if (user && permissions) {
    const requestedPath = getRequestedPath();
    if (requestedPath) {
      return <Navigate to={requestedPath} replace />;
    }

    const userRole = user.role;
    if (isSalesUserRole(userRole)) {
      return <Navigate to={isMobileClient() ? "/mobile/orders" : "/orders"} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
};

const MarketsTaxesRedirect = () => {
  const location = useLocation();

  return (
    <Navigate
      replace
      to={location.pathname.replace("/markets/taxes", "/markets/taxes-fees")}
    />
  );
};

const ProtectedRoute = (props: {
  id?: keyof PermissionsParsed;
  roles?: string[];
  children: ReactElement;
}) => {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);
  const location = useLocation();

  if (user && permissions) {
    const allowedRoles = props.roles?.map((role) => role.toLowerCase());
    const userRole = user.role?.toLowerCase();

    if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
      return <Navigate to="/notfound" replace />;
    }

    if (props.id && !permissions[props.id]?.isRead) {
      return <Navigate to="/notfound" replace />;
    }

    if (props.id && permissions[props.id]?.isRead) {
      return (
        <>
          <PageVideoPopupHost />
          {props.children}
        </>
      );
    }

    if (!props.id) {
      return (
        <>
          <PageVideoPopupHost />
          {props.children}
        </>
      );
    }
    return <Navigate to="/notfound" replace />;
  }
  return (
    <Navigate
      replace
      state={{
        from: {
          hash: location.hash,
          pathname: location.pathname,
          search: location.search,
        },
      }}
      to="/login"
    />
  );
};

const NotFoundRoute = (props: { children: ReactElement }) => {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);

  if (user && permissions) {
    return props.children;
  }
  return <Navigate to="/" replace />;
};
export const routes: RouteObject[] = [
  {
    element: <PublicRoute />,
    children: [
      {
        path: "/",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "/login",
        async lazy() {
          let { Login } = await import("features/auth/views/Login.tsx");
          return { Component: Login };
        },
      },
      {
        path: "*",
        element: <Navigate to="/login" replace />,
      },
    ],
  },
  {
    path: "/",
    async lazy() {
      let { Dashboard } = await import(
        "features/dashboard/views/Dashboard.tsx"
      );
      return {
        Component: () => {
          const user = useAppSelector(selectUser);

          return (
          <ProtectedRoute id="DashboardPage">
            {isSalesUserRole(user?.role) ? (
              <Navigate
                replace
                to={isMobileClient() ? "/mobile/orders" : "/orders"}
              />
            ) : (
              <Dashboard />
            )}
          </ProtectedRoute>
          );
        },
      };
    },
  },
  {
    path: "/dashboard",
    async lazy() {
      let { Dashboard } = await import(
        "features/dashboard/views/Dashboard.tsx"
      );
      return {
        Component: () => {
          const user = useAppSelector(selectUser);

          return (
          <ProtectedRoute id="DashboardPage">
            {isSalesUserRole(user?.role) ? (
              <Navigate
                replace
                to={isMobileClient() ? "/mobile/orders" : "/orders"}
              />
            ) : (
              <Dashboard />
            )}
          </ProtectedRoute>
          );
        },
      };
    },
  },
  {
    path: "/summary",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/release-notes",
    async lazy() {
      let { ReleaseNotes } = await import(
        "features/releaseNotes/views/ReleaseNotes.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute>
            <ReleaseNotes />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/orders",
    async lazy() {
      let { Orders } = await import("features/orders/views/Orders.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <Orders variant="desktop" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/orders/edit/:id",
    handle: "Orders",
    async lazy() {
      let { OrderEditor } = await import(
        "features/orders/views/OrderEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <OrderEditor variant="desktop" />
          </ProtectedRoute>
        ),
      };
    },
  },

  {
    path: "/mobile/orders",
    async lazy() {
      let { Orders } = await import("features/orders/views/Orders.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <Orders variant="mobile" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/mobile/orders/edit/:id",
    handle: "Orders",
    async lazy() {
      let { OrderEditor } = await import(
        "features/orders/views/OrderEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <OrderEditor variant="mobile" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/mobile/orders/bulkupload",
    handle: "Orders",
    async lazy() {
      let { OrdersBulkUploader: OrdersBulkUploader } = await import(
        "features/ordersBulkUploads/views/OrdersBulkUploader"
      );
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <OrdersBulkUploader />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/orders/bulkupload",
    handle: "Orders",
    async lazy() {
      let { OrdersBulkUploader: OrdersBulkUploader } = await import(
        "features/ordersBulkUploads/views/OrdersBulkUploader"
      );
      return {
        Component: () => (
          <ProtectedRoute id="OrdersPage">
            <OrdersBulkUploader />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/tickets",
    async lazy() {
      let { Tickets } = await import("features/tickets/views/Tickets.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="TicketsPage">
            <Tickets variant="desktop" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/tickets/edit/:id",
    handle: "Tickets",
    async lazy() {
      let { TicketEditor } = await import(
        "features/tickets/views/TicketEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketsPage">
            <TicketEditor variant="desktop" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/mobile/tickets",
    async lazy() {
      let { Tickets } = await import("features/tickets/views/Tickets.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="TicketsPage">
            <Tickets variant="mobile" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/mobile/tickets/edit/:id",
    handle: "Tickets",
    async lazy() {
      let { TicketEditor } = await import(
        "features/tickets/views/TicketEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketsPage">
            <TicketEditor variant="mobile" />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers",
    async lazy() {
      let { Customers } = await import(
        "features/customers/views/Customers.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <Customers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/edit/:id",
    handle: "Customers",
    async lazy() {
      let { CustomerEditor } = await import(
        "features/customers/views/CustomerEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <CustomerEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/contacts",
    async lazy() {
      let { CustomerContacts } = await import(
        "features/customerContacts/views/CustomerContacts.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <CustomerContacts />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/contacts/edit/:id",
    handle: "Customer Contacts",
    async lazy() {
      let { CustomerContactEditor } = await import(
        "features/customerContacts/views/CustomerContactEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <CustomerContactEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/basins",
    async lazy() {
      let { Basins } = await import("features/basins/views/Basins.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <Basins />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/rigs",
    async lazy() {
      let { Rigs } = await import("features/rigs/views/Rigs.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Rigs">
            <Rigs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/rigs/edit/:id",
    handle: "Rigs",
    async lazy() {
      let { RigEditor } = await import("features/rigs/views/RigEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Rigs">
            <RigEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/mailers",
    async lazy() {
      let { CustomerMailers } = await import(
        "features/customerMailers/views/CustomerMailers.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <CustomerMailers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/customers/mailers/edit/:id",
    handle: "Customer Mailer",
    async lazy() {
      let { CustomerMailerEditor } = await import(
        "features/customerMailers/views/CustomerMailerEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CustomersPage">
            <CustomerMailerEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/drivers",
    async lazy() {
      let { Drivers } = await import("features/drivers/views/Drivers.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="DriversPage">
            <Drivers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/drivers/edit/:id",
    handle: "Drivers",
    async lazy() {
      let { DriverEditor } = await import(
        "features/drivers/views/DriverEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="DriversPage">
            <DriverEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/pricesmap",
    async lazy() {
      let { PriceMapTwo } = await import(
        "features/opis/views/PriceMapTwo.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PRICES">
            <PriceMapTwo />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/pricesmap/mailers",
    async lazy() {
      let { PriceMapMailers } = await import(
        "features/opis/views/PriceMapMailers.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PRICES">
            <PriceMapMailers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/pricesmap/snapshot",
    async lazy() {
      let { PriceMapSnapshot } = await import(
        "features/opis/views/PriceMapSnapshot.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PRICES">
            <PriceMapSnapshot />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/pricesmap/customer-price-lookup",
    async lazy() {
      let { CustomerPriceLookup } = await import(
        "features/opis/views/CustomerPriceLookup.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PRICES">
            <CustomerPriceLookup />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols",
    async lazy() {
      let { BOLs } = await import("features/bols/views/BOLs.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="BOLS">
            <BOLs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/edit/:id",
    handle: "BOLs",
    async lazy() {
      let { BOLEditor } = await import("features/bols/views/BOLEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="BOLS">
            <BOLEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/markets",
    async lazy() {
      let { BOLMarkets } = await import(
        "features/bolMarkets/views/BOLMarkets.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="BOL MARKETS">
            <BOLMarkets />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/markets/edit/:id",
    handle: "BOL Markets",
    async lazy() {
      let { BOLMarketEditor } = await import(
        "features/bolMarkets/views/BOLMarketEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="BOL MARKETS">
            <BOLMarketEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/counties",
    async lazy() {
      let { BOLCounties } = await import(
        "features/bolCounties/views/BOLCounties.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="BOL COUNTIES">
            <BOLCounties />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/counties/edit/:id",
    handle: "BOL Counties",
    async lazy() {
      let { BOLCountyEditor } = await import(
        "features/bolCounties/views/BOLCountyEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="BOL COUNTIES">
            <BOLCountyEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/carriers",
    async lazy() {
      let { Carriers } = await import("features/carriers/views/Carriers.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="CARRIERS">
            <Carriers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/carriers/edit/:id",
    handle: "Carriers",
    async lazy() {
      let { CarrierEditor } = await import(
        "features/carriers/views/CarrierEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="CARRIERS">
            <CarrierEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/refineries",
    async lazy() {
      let { Refineries } = await import(
        "features/refineries/views/Refineries.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="REFINERIES">
            <Refineries />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/refineries/edit/:id",
    handle: "Refineries",
    async lazy() {
      let { RefineryEditor } = await import(
        "features/refineries/views/RefineryEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="REFINERIES">
            <RefineryEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/refineryAddresses",
    async lazy() {
      let { RefineryAddresses } = await import(
        "features/refineryAddresses/views/RefineryAddresses.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="REFINERY ADDRESSES">
            <RefineryAddresses />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/refineryAddresses/edit/:id",
    handle: "Refinery Addresses",
    async lazy() {
      let { RefineryAddressEditor } = await import(
        "features/refineryAddresses/views/RefineryAddressEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="REFINERY ADDRESSES">
            <RefineryAddressEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/vendors",
    async lazy() {
      let { Vendors } = await import("features/vendors/views/Vendors.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="VENDORS">
            <Vendors />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/bols/vendors/edit/:id",
    handle: "Vendors",
    async lazy() {
      let { VendorEditor } = await import(
        "features/vendors/views/VendorEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="VENDORS">
            <VendorEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets",
    async lazy() {
      let { Markets } = await import("features/markets/views/Markets.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Markets">
            <Markets />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/edit/:id",
    handle: "Markets",
    async lazy() {
      let { MarketEditor } = await import(
        "features/markets/views/MarketEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="Markets">
            <MarketEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/counties",
    async lazy() {
      let { Counties } = await import("features/counties/views/Counties.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Markets">
            <Counties />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/counties/edit/:id",
    handle: "Counties",
    async lazy() {
      let { CountyEditor } = await import(
        "features/counties/views/CountyEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="Markets">
            <CountyEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/loadorigins",
    async lazy() {
      let { LoadOrigins } = await import(
        "features/loadOrigins/views/LoadOrigins.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="LOADORIGIN">
            <LoadOrigins />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/loadorigins/edit/:id",
    handle: "Load Origins",
    async lazy() {
      let { LoadOriginEditor } = await import(
        "features/loadOrigins/views/LoadOriginEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="LOADORIGIN">
            <LoadOriginEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/products",
    async lazy() {
      let { Products } = await import("features/products/views/Products.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="ProductsPage">
            <Products />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/products/edit/:id",
    handle: "Products",
    async lazy() {
      let { ProductEditor } = await import(
        "features/products/views/ProductEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="ProductsPage">
            <ProductEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/jobtypes",
    async lazy() {
      let { JobTypes } = await import("features/jobTypes/views/JobTypes.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="JobTypes">
            <JobTypes />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/jobtypegroups",
    async lazy() {
      let { JobTypeGroups } = await import(
        "features/jobTypeGroups/views/JobTypeGroups.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="JobTypes">
            <JobTypeGroups />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/jobtypes/edit/:id",
    handle: "Job Types",
    async lazy() {
      let { JobTypeEditor } = await import(
        "features/jobTypes/views/JobTypeEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="JobTypes">
            <JobTypeEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/jobtypegroups/edit/:id",
    handle: "Job Type Groups",
    async lazy() {
      let { JobTypeGroupEditor } = await import(
        "features/jobTypeGroups/views/jobTypeGroupEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="JobTypeGroups">
            <JobTypeGroupEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/tickettypes",
    async lazy() {
      let { TicketTypes } = await import(
        "features/ticketTypes/views/TicketTypes.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketAdminPage">
            <TicketTypes />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/tickettypes/edit/:id",
    handle: "Ticket Types",
    async lazy() {
      let { TicketTypeEditor } = await import(
        "features/ticketTypes/views/TicketTypeEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketAdminPage">
            <TicketTypeEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/units",
    async lazy() {
      let { Units } = await import("features/units/views/Units.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Units">
            <Units />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/units/edit/:id",
    handle: "Units",
    async lazy() {
      let { UnitEditor } = await import("features/units/views/UnitEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Units">
            <UnitEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/taxes",
    Component: MarketsTaxesRedirect,
  },
  {
    path: "/markets/taxes-fees",
    async lazy() {
      let { Taxes } = await import("features/taxes/views/Taxes.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Taxes">
            <Taxes />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/taxes/edit/:id",
    Component: MarketsTaxesRedirect,
  },
  {
    path: "/markets/taxes-fees/edit/:id",
    handle: "Taxes/Fees",
    async lazy() {
      let { TaxEditor } = await import("features/taxes/views/TaxEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Taxes">
            <TaxEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/product-taxes",
    async lazy() {
      let { ProductTaxes } = await import("features/admin/views/ProductTaxes.tsx");
      return { Component: () => <ProtectedRoute id="Taxes"><ProductTaxes /></ProtectedRoute> };
    },
  },
  {
    path: "/markets/trucks",
    async lazy() {
      let { Trucks } = await import("features/trucks/views/Trucks.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="TruckPage">
            <Trucks />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/markets/trucks/edit/:id",
    handle: "Trucks",
    async lazy() {
      let { TruckEditor } = await import(
        "features/trucks/views/TruckEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TruckPage">
            <TruckEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users",
    async lazy() {
      let { Users } = await import("features/users/views/Users.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Users">
            <Users />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/edit/:id",
    handle: "Users",
    async lazy() {
      let { UserEditor } = await import("features/users/views/UserEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Users">
            <UserEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/salesreps",
    async lazy() {
      let { SalesReps } = await import(
        "features/salesReps/views/SalesReps.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SalesReps">
            <SalesReps />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/salesreps/edit/:id",
    handle: "Sales Reps",
    async lazy() {
      let { SalesRepEditor } = await import(
        "features/salesReps/views/SalesRepEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SalesReps">
            <SalesRepEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/roles",
    async lazy() {
      let { Roles } = await import("features/roles/views/Roles.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Roles">
            <Roles />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/roles/edit/:id",
    handle: "Roles",
    async lazy() {
      let { RoleEditor } = await import("features/roles/views/RoleEditor.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="Roles">
            <RoleEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/resources",
    async lazy() {
      let { Resources } = await import(
        "features/resources/views/Resources.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PageModules">
            <Resources />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/users/resources/edit/:id",
    handle: "Resources",
    async lazy() {
      let { ResourceEditor } = await import(
        "features/resources/views/ResourceEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="PageModules">
            <ResourceEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin",
    async lazy() {
      let { InvoiceUpdate } = await import(
        "features/admin/views/InvoiceUpdate.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <InvoiceUpdate />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/product-taxes",
    async lazy() {
      let { ProductTaxes } = await import(
        "features/admin/views/ProductTaxes.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <ProductTaxes />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/cron-jobs",
    async lazy() {
      let { CronJobs } = await import("features/admin/views/CronJobs.tsx");
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <CronJobs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/tools-utilities",
    async lazy() {
      let { AdminMisc } = await import("features/admin/views/AdminMisc.tsx");
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <AdminMisc />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/typesense",
    async lazy() {
      let { TypesenseAdmin } = await import(
        "features/admin/views/TypesenseAdmin.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <TypesenseAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/ticket-pdf-exports",
    lazy: async () => {
      let { TicketPdfExportsAdmin } = await import(
        "features/admin/views/TicketPdfExportsAdmin.tsx"
      );
      return {
        element: (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <TicketPdfExportsAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/misc",
    element: <Navigate replace to="/admin/tools-utilities" />,
  },
  {
    path: "/admin/release-notes",
    async lazy() {
      let { ReleaseNotesAdmin } = await import(
        "features/releaseNotes/views/ReleaseNotesAdmin.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <ReleaseNotesAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/login-notice",
    async lazy() {
      let { LoginNoticeAdmin } = await import(
        "features/loginNotice/views/LoginNoticeAdmin.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <LoginNoticeAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/mock-emails",
    handle: "Mock Emails",
    async lazy() {
      let { MockEmails } = await import("features/admin/views/MockEmails.tsx");
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]}>
            <MockEmails />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/system-alerts",
    async lazy() {
      let { SystemAlertsAdmin } = await import(
        "features/systemAlerts/views/SystemAlertsAdmin.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["sysadmin"]}>
            <SystemAlertsAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/page-video-popups",
    async lazy() {
      let { PageVideoPopupsAdmin } = await import(
        "features/pageVideoPopups/views/PageVideoPopupsAdmin.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["sysadmin"]}>
            <PageVideoPopupsAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/audit",
    async lazy() {
      let { AuditAdmin } = await import("features/admin/views/AuditAdmin.tsx");
      return {
        Component: () => (
          <ProtectedRoute roles={["sysadmin"]}>
            <AuditAdmin />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/sql-migrations",
    async lazy() {
      let { SqlMigrations } = await import(
        "features/admin/views/SqlMigrations.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["sysadmin"]}>
            <SqlMigrations />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings",
    async lazy() {
      let { Settings } = await import("features/settings/views/Settings.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <Settings />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/edit/:id",
    handle: "Settings",
    async lazy() {
      let { SettingEditor } = await import(
        "features/settings/views/SettingEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <SettingEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/mailertemplates",
    async lazy() {
      let { MailerTemplates } = await import(
        "features/mailerTemplates/views/MailerTemplates.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <MailerTemplates />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/companies",
    async lazy() {
      let { Companies } = await import(
        "features/companies/views/Companies.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <Companies />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/companies/edit/:id",
    handle: "Companies",
    async lazy() {
      let { CompanyEditor } = await import(
        "features/companies/views/CompanyEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <CompanyEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/mailertemplates/edit",
    handle: "Mailer Templates",
    async lazy() {
      let { MailerTemplateEditor } = await import(
        "features/mailerTemplates/views/MailerTemplateEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <MailerTemplateEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/mailertemplates/edit/:id",
    handle: "Mailer Templates",
    async lazy() {
      let { MailerTemplateEditor } = await import(
        "features/mailerTemplates/views/MailerTemplateEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <MailerTemplateEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/admin/mock-emails/:id",
    handle: "Mock Email",
    async lazy() {
      let { MockEmailDetail } = await import(
        "features/admin/views/MockEmailDetail.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <MockEmailDetail />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/ticket-papers",
    async lazy() {
      let { TicketPapers } = await import(
        "features/ticketPapers/views/TicketPapers.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]} id="TicketPapersPage">
            <TicketPapers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/ticket-papers/edit/:id",
    handle: "Ticket Print Templates",
    async lazy() {
      let { TicketPaperEditor } = await import(
        "features/ticketPapers/views/TicketPaperEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]} id="TicketPapersPage">
            <TicketPaperEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/notificationtemplates",
    handle: "Notification Templates",
    async lazy() {
      let { NotificationTemplates } = await import(
        "features/notificationTemplates/views/NotificationTemplates.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <NotificationTemplates />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/notificationtemplates/edit/:id",
    handle: "Notification Templates",
    async lazy() {
      let { NotificationTemplateEditor } = await import(
        "features/notificationTemplates/views/NotificationTemplateEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <NotificationTemplateEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/shipping-papers",
    handle: "Shipping Papers",
    async lazy() {
      let { ShippingPapers } = await import(
        "features/shippingPapers/views/ShippingPapers.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]} id="ShippingPapersPage">
            <ShippingPapers />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/shipping-papers/edit/:id",
    handle: "Shipping Papers",
    async lazy() {
      let { ShippingPaperEditor } = await import(
        "features/shippingPapers/views/ShippingPaperEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute roles={["admin", "sysadmin"]} id="ShippingPapersPage">
            <ShippingPaperEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/timeslots",
    async lazy() {
      let { TimeSlots } = await import(
        "features/timeSlots/views/TimeSlots.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketAdminPage">
            <TimeSlots />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/timeslots/edit/:id",
    handle: "Time Slots",
    async lazy() {
      let { TimeSlotEditor } = await import(
        "features/timeSlots/views/TimeSlotEditor.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="TicketAdminPage">
            <TimeSlotEditor />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/customerlogs",
    async lazy() {
      let { CustomerLogs } = await import(
        "features/customerLogs/views/CustomerLogs.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <CustomerLogs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/orderlogs",
    async lazy() {
      let { OrderLogs } = await import(
        "features/orderLogs/views/OrderLogs.tsx"
      );
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <OrderLogs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/settings/riglogs",
    async lazy() {
      let { RigLogs } = await import("features/rigLogs/views/RigLogs.tsx");
      return {
        Component: () => (
          <ProtectedRoute id="SettingsList">
            <RigLogs />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "/notfound",
    async lazy() {
      let { PageNotFound } = await import(
        "components/organisms/PageNotFound/PageNotFound.tsx"
      );
      return {
        Component: () => (
          <NotFoundRoute>
            <PageNotFound />
          </NotFoundRoute>
        ),
      };
    },
  },
];
